import { Paginator, Message, Participant, User } from '@twilio/conversations';
import { differenceInDays, isToday, startOfDay } from 'date-fns';
import { from, fromEvent, NEVER, Observable, Subject } from 'rxjs';
import { tap, takeUntil, switchMap } from 'rxjs/operators';

import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { SnChatUser, SnChannel, SnMessage } from '../../models';
import { SnMessagesService } from '../../services';

@Component({
  selector: 'sn-message-list',
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss'],
})
export class SnMessageListComponent implements OnInit, OnDestroy {
  public readonly fetchMessages = this._fetchMessages.bind(this);

  @Input()
  get channel(): SnChannel {
    return this._channel;
  }
  set channel(value: SnChannel) {
    this._destroy$.next();
    this._channel = value;
    this.isLoading = true;
    this._getMessages();
  }

  public typingMembers: string[] = [];

  public isLoading = false;
  public isFetching = false;

  private _channel: SnChannel;

  private _lastPaginator: Paginator<Message>;

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _messagesService: SnMessagesService) {}

  public ngOnInit(): void {
    fromEvent(this.channel.twilioChannel, 'typingStarted')
      .pipe(
        switchMap((twilioMember: Participant) => {
          return from(twilioMember.getUser());
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((user: User) => {
        this.typingMembers.push(user.friendlyName);
        this.typingMembers = [...new Set(this.typingMembers)];
      });

    fromEvent(this.channel.twilioChannel, 'typingEnded')
      .pipe(
        switchMap((twilioMember: Participant) => {
          return from(twilioMember.getUser());
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((user: User) => {
        const index = this.typingMembers.findIndex((member) => member === user.friendlyName);

        if (index !== -1) {
          this.typingMembers.splice(index, 1);
          this.typingMembers = this.typingMembers.slice();
        }
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackMessage(index: number, item: SnMessage): any {
    return item.id;
  }

  public isToday(date: Date): boolean {
    return isToday(date);
  }

  public isMessageInherited(currentIndex: number): boolean {
    if (currentIndex !== this._channel.messages.length - 1) {
      const currentMessage = this._channel.messages[currentIndex];
      const nextMessage = this._channel.messages[currentIndex + 1];

      return (
        currentMessage.twilio.author === nextMessage.twilio.author &&
        !differenceInDays(
          startOfDay(nextMessage.twilio.dateCreated),
          startOfDay(currentMessage.twilio.dateCreated),
        )
      );
    }

    return false;
  }

  public isMessageFirstInDay(currentIndex: number): boolean {
    if (currentIndex !== this._channel.messages.length - 1) {
      const currentMessage = this._channel.messages[currentIndex];
      const nextMessage = this._channel.messages[currentIndex + 1];

      return !!differenceInDays(
        startOfDay(nextMessage.twilio.dateCreated),
        startOfDay(currentMessage.twilio.dateCreated),
      );
    }

    return true;
  }

  public getMessageAuthor(userSid: string): SnChatUser {
    return this._channel.members.find((user: SnChatUser) => user.chatUser?.id === userSid);
  }

  private _getMessages(): void {
    this._messagesService
      .getMessages(this._channel)
      .pipe(
        tap(({ items }: Paginator<Message>) => {
          const messages = items
            .map((item) => {
              const message = new SnMessage(item);
              const oldMessage = this._channel.messages.get(message.id);

              if (oldMessage) {
                message.mediaSource = oldMessage.mediaSource;
              }

              return message;
            })
            .reverse();
          this._channel.messages.set(...messages);
          this.isLoading = false;

          if (items.length) {
            const lastMessageIndex = items[items.length - 1].index;
            this._channel.twilioChannel.updateLastReadMessageIndex(lastMessageIndex);
          }
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((messagePaginator) => {
        this._lastPaginator = messagePaginator;
      });
  }

  private _fetchMessages(): Observable<Paginator<Message>> {
    if (this._lastPaginator && this._lastPaginator.hasPrevPage) {
      this.isFetching = true;
      return from(this._lastPaginator.prevPage()).pipe(
        tap(({ items }: Paginator<Message>) => {
          const fwMessages = items.map((item) => new SnMessage(item)).reverse();
          this._channel.messages.set(...fwMessages);
        }),
        tap((messagePaginator: Paginator<Message>) => {
          this._lastPaginator = messagePaginator;
          this.isFetching = false;
        }),
        takeUntil(this._destroy$),
      );
    }

    return NEVER;
  }
}
